// src/components/common/StandardButtons.jsx
import React from 'react';
import T from './T';
import { ArrowPathIcon, PlusIcon } from '@heroicons/react/24/outline';

/**
 * Standard Primary Action Button (e.g. + Add Task, + New Project)
 * Follows enterprise styling with uniform height, font, and brand color.
 * On mobile (< sm): Compact square icon-only button (w-8 h-8)
 * On desktop (>= sm): Full button with icon and uppercase text label
 */
export const TopPrimaryButton = ({
  onClick,
  icon: Icon = PlusIcon,
  label = "Add New",
  children,
  disabled = false,
  loading = false,
  className = ""
}) => {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      className={`h-8 w-8 sm:w-auto px-0 sm:px-3.5 bg-[#E8612C] text-white rounded-lg text-[11px] font-bold uppercase tracking-wider hover:bg-[#d05322] active:scale-[0.98] transition shadow-2xs flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-50 shrink-0 ${className}`}
      title={typeof label === 'string' ? label : undefined}
      aria-label={typeof label === 'string' ? label : undefined}
    >
      {Icon && <Icon className={`w-4 h-4 sm:w-3.5 sm:h-3.5 ${loading ? 'animate-spin' : ''}`} strokeWidth={2.2} />}
      <span className="hidden sm:inline">{children || <T>{label}</T>}</span>
    </button>
  );
};

/**
 * Standard Secondary / Outline Button (e.g. Filters, Export, Refresh)
 * On mobile (< sm): Compact square icon-only button (w-8 h-8)
 * On desktop (>= sm): Full button with icon and uppercase text label
 */
export const TopSecondaryButton = ({
  onClick,
  icon: Icon,
  label,
  children,
  disabled = false,
  active = false,
  className = ""
}) => {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`h-8 w-8 sm:w-auto px-0 sm:px-3 rounded-lg border text-[11px] font-bold uppercase tracking-wide transition shadow-2xs flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-50 shrink-0 ${
        active
          ? 'bg-[#E8612C]/10 border-[#E8612C] text-[#E8612C] dark:bg-[#E8612C]/20'
          : 'bg-white dark:bg-[#21262d] border-gray-300 dark:border-[#30363d] text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-800'
      } ${className}`}
      title={typeof label === 'string' ? label : undefined}
      aria-label={typeof label === 'string' ? label : undefined}
    >
      {Icon && <Icon className="w-4 h-4 sm:w-3.5 sm:h-3.5" strokeWidth={2} />}
      <span className="hidden sm:inline">{children || (label ? <T>{label}</T> : null)}</span>
    </button>
  );
};

/**
 * Standard Refresh Button matching General Ledger exactly
 */
export const TopRefreshButton = ({
  onRefresh,
  loading = false,
  className = ""
}) => {
  return (
    <TopSecondaryButton
      onClick={onRefresh}
      disabled={loading}
      icon={ArrowPathIcon}
      label="Refresh"
      className={className}
    />
  );
};
